<div class="w-full h-full flex flex-col p-6 bg-base-200 min-h-screen">
  <div class="bg-base-100 rounded-lg shadow p-6">
    <!-- Header + Filters -->
    <div class="border-b border-base-300 pb-4 mb-4">
      <div class="flex flex-wrap justify-between items-center gap-4 mb-4">
        <div>
          <div class="text-2xl font-bold text-base-content">Orders</div>
          <p class="text-sm text-base-content/60">
            Orders and requests raised by sales reps. Urgent orders are listed first.
          </p>
        </div>
        <button class="btn btn-ghost btn-sm" (click)="clearFilters()">Clear filters</button>
      </div>

      <div class="flex flex-wrap items-end gap-2">
        <label class="form-control">
          <span class="label-text text-xs">Order ID</span>
          <input
            type="search"
            class="input input-bordered input-sm w-44"
            placeholder="Full or partial ID"
            [(ngModel)]="orderId"
            (ngModelChange)="onOrderIdSearch()"
            (keyup.enter)="onFilterChange()"
            (search)="onFilterChange()"
          />
        </label>

        <label class="form-control">
          <span class="label-text text-xs">Status</span>
          <select
            class="select select-bordered select-sm"
            [(ngModel)]="selectedStatus"
            (ngModelChange)="onFilterChange()"
          >
            <option value="">All</option>
            @for (status of statuses; track status) {
              <option [value]="status">{{ status }}</option>
            }
          </select>
        </label>

        <label class="form-control">
          <span class="label-text text-xs">Type</span>
          <select
            class="select select-bordered select-sm"
            [(ngModel)]="selectedType"
            (ngModelChange)="onFilterChange()"
          >
            <option value="">All</option>
            @for (type of types; track type) {
              <option [value]="type">{{ type }}</option>
            }
          </select>
        </label>

        <!-- Customer typeahead -->
        <label class="form-control relative">
          <span class="label-text text-xs">Customer</span>
          @if (selectedCustomer) {
            <div class="input input-bordered input-sm flex items-center gap-2 pr-1 w-48">
              <span class="truncate flex-1" [title]="selectedCustomer.name">
                {{ selectedCustomer.name }}
              </span>
              <button
                class="btn btn-ghost btn-xs"
                aria-label="Clear customer filter"
                (click)="selectCustomerFilter(null)"
              >
                ✕
              </button>
            </div>
          } @else {
            <input
              type="text"
              autocomplete="off"
              class="input input-bordered input-sm w-48"
              placeholder="Name, address or phone"
              (input)="onCustomerSearch($any($event.target).value)"
            />
            @if (customerOptions !== null) {
              <ul
                class="menu bg-base-100 rounded-box absolute top-full left-0 z-10 mt-1 w-64 p-2 shadow max-h-64 overflow-y-auto flex-nowrap"
              >
                @for (customer of customerOptions; track customer.id) {
                  <li>
                    <button class="text-left truncate" (click)="selectCustomerFilter(customer)">
                      {{ customer.name }}
                    </button>
                  </li>
                } @empty {
                  <li class="menu-title">No customers found</li>
                }
              </ul>
            }
          }
        </label>

        <!-- Sales rep typeahead -->
        <label class="form-control relative">
          <span class="label-text text-xs">Sales rep</span>
          @if (selectedRep) {
            <div class="input input-bordered input-sm flex items-center gap-2 pr-1 w-48">
              <span class="truncate flex-1" [title]="selectedRep.email">
                {{ selectedRep.name }}
              </span>
              <button
                class="btn btn-ghost btn-xs"
                aria-label="Clear sales rep filter"
                (click)="selectRepFilter(null)"
              >
                ✕
              </button>
            </div>
          } @else {
            <input
              type="text"
              autocomplete="off"
              class="input input-bordered input-sm w-48"
              placeholder="Search by name or email"
              (input)="onRepSearch($any($event.target).value)"
            />
            @if (repOptions !== null) {
              <ul
                class="menu bg-base-100 rounded-box absolute top-full left-0 z-10 mt-1 w-64 p-2 shadow max-h-64 overflow-y-auto flex-nowrap"
              >
                @for (rep of repOptions; track rep.id) {
                  <li>
                    <button class="text-left" (click)="selectRepFilter(rep)">
                      <span class="truncate">{{ rep.name }}</span>
                      <span class="text-xs text-base-content/60 truncate">{{ rep.email }}</span>
                    </button>
                  </li>
                } @empty {
                  <li class="menu-title">No reps found</li>
                }
              </ul>
            }
          }
        </label>

        <label class="form-control">
          <span class="label-text text-xs">Updated from</span>
          <input
            type="date"
            class="input input-bordered input-sm"
            [(ngModel)]="from"
            (ngModelChange)="onFilterChange()"
          />
        </label>

        <label class="form-control">
          <span class="label-text text-xs">Updated to</span>
          <input
            type="date"
            class="input input-bordered input-sm"
            [(ngModel)]="to"
            (ngModelChange)="onFilterChange()"
          />
        </label>

        <label class="form-control">
          <span class="label-text text-xs">Page size</span>
          <select
            class="select select-bordered select-sm"
            [ngModel]="pageSize"
            (ngModelChange)="onPageSizeChange($event)"
          >
            @for (size of [5, 10, 25, 50]; track size) {
              <option [ngValue]="size">{{ size }}</option>
            }
          </select>
        </label>
      </div>
    </div>

    <!-- Category Tabs -->
    <div role="tablist" class="tabs tabs-box mb-4 w-fit">
      <button
        role="tab"
        class="tab"
        [class.tab-active]="orderCategory === null"
        (click)="selectCategory(null)"
      >
        All
      </button>
      @for (category of categoryTabs; track category) {
        <button
          role="tab"
          class="tab"
          [class.tab-active]="orderCategory === category"
          (click)="selectCategory(category)"
        >
          {{ category.replace('_', ' ') }}
        </button>
      }
    </div>

    @if (error) {
      <div class="alert alert-error mb-4">{{ error }}</div>
    }

    @if (orders === null) {
      <div class="flex justify-center p-8">
        <span class="loading loading-spinner loading-lg"></span>
      </div>
    } @else if (orders.length === 0) {
      <div class="text-center text-base-content/60 p-8">No orders found.</div>
    } @else {
      <div class="overflow-x-auto">
        <table class="table table-zebra">
          <thead>
            <tr>
              <th>#</th>
              <th class="text-right">Item Count</th>
              <th>Type</th>
              <th>Category</th>
              <th>Status</th>
              <th>Customer</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Updated By</th>
              <th class="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            @for (order of orders; track order.id; let i = $index) {
              <tr class="hover">
                <td>{{ page * pageSize + i + 1 }}</td>
                <td class="text-right">{{ order.contents?.length || 0 }}</td>
                <td class="uppercase">
                  {{ order.type }}
                  @if (order.urgent) {
                    <span class="badge badge-error badge-sm font-semibold">URGENT</span>
                  }
                </td>
                <td>{{ order.category?.replace('_', ' ') || '—' }}</td>
                <td>
                  <span
                    class="badge"
                    [class.badge-success]="order.status === 'APPROVED'"
                    [class.badge-warning]="order.status === 'PENDING'"
                    [class.badge-error]="order.status === 'REJECTED'"
                    [class.badge-ghost]="order.status === 'CANCELLED'"
                  >
                    {{ order.status }}
                  </span>
                </td>
                <td>
                  @if (order.customerId) {
                    <a
                      class="link link-hover"
                      [routerLink]="['/customers', order.customerId]"
                      target="_blank"
                      rel="noopener"
                    >
                      {{ order.customerName || '—' }}
                    </a>
                  } @else {
                    {{ order.customerName || '—' }}
                  }
                </td>
                <td>{{ order.createdAt | date: 'medium' }}</td>
                <td>{{ order.updatedAt | date: 'short' }}</td>
                <td>{{ resolveUserName(order.updatedBy) }}</td>
                <td class="text-center">
                  <button class="btn btn-sm btn-ghost" (click)="showOrderInfo(order)">Info</button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

    <!-- Pagination -->
    @if (totalPages > 1) {
      <div class="flex justify-between items-center mt-4">
        <span class="text-sm text-base-content/60">{{ totalElements }} orders</span>
        <div class="join">
          <button class="join-item btn btn-sm" [disabled]="page === 0" (click)="goToPage(page - 1)">
            «
          </button>
          <button class="join-item btn btn-sm">Page {{ page + 1 }} of {{ totalPages }}</button>
          <button
            class="join-item btn btn-sm"
            [disabled]="page + 1 >= totalPages"
            (click)="goToPage(page + 1)"
          >
            »
          </button>
        </div>
      </div>
    }
  </div>
</div>

<app-action-dialog [order]="actioningOrder" (onClose)="onActioningOrderDeselected()" />
